import { useRef, useState } from "react"
import { FileText, Trash2, Upload } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useContracts } from "@/features/contracts/hooks/use-contracts"
import { useCreateQCLog } from "@/features/lots/hooks/use-lot-mutations"
import type { QCDecision, QCKind } from "@/features/lots/api/types"
import { uploadDocument } from "@/features/documents/lib/upload-helpers"
import { extractApiErrorMessage } from "@/lib/api/client"
import { toast } from "sonner"

interface AddQCLogDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lotId: string | number
  defaultKind: QCKind
  onCreated?: (kind: QCKind) => void
}

interface PendingFile {
  file: File
  title?: string
}

export function AddQCLogDialog({
  open,
  onOpenChange,
  lotId,
  defaultKind,
  onCreated,
}: AddQCLogDialogProps) {
  const { t } = useTranslation("lots")
  const { data: contracts } = useContracts()
  const create = useCreateQCLog(lotId)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [kind, setKind] = useState<QCKind>(defaultKind)
  const [contractId, setContractId] = useState<string>("none")
  const [decision, setDecision] = useState<QCDecision>("PENDING")
  const [reason, setReason] = useState("")
  const [note, setNote] = useState("")
  const [files, setFiles] = useState<PendingFile[]>([])
  const [uploading, setUploading] = useState(false)

  const reset = () => {
    setKind(defaultKind)
    setContractId("none")
    setDecision("PENDING")
    setReason("")
    setNote("")
    setFiles([])
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []).map((file) => ({ file }))
    if (picked.length === 0) return
    setFiles((prev) => [...prev, ...picked])
    if (e.target) e.target.value = ""
  }

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx))

  const submit = async () => {
    setUploading(true)
    try {
      // Create the QC log first — we need its real `logId` before
      // uploading documents (the documents service writes the entry
      // into that log's `documents` JSONB array server-side).
      const created = await create.mutateAsync({
        kind,
        contractId: contractId === "none" ? undefined : Number(contractId),
        decision,
        reason: reason.trim() || undefined,
        note: note.trim() || undefined,
      })

      for (const pf of files) {
        await uploadDocument({
          file: pf.file,
          level: "lotQc",
          title: pf.title ?? pf.file.name,
          lotId: String(lotId),
          logId: String(created.id),
        })
      }

      onCreated?.(kind)
      handleOpenChange(false)
    } catch (err) {
      toast.error(extractApiErrorMessage(err))
    } finally {
      setUploading(false)
    }
  }

  const isPending = uploading || create.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {kind === "LAB"
              ? t("detail.qcModal.addLab")
              : kind === "CLIENT"
                ? t("detail.qcModal.addClient")
                : t("detail.qcModal.addOther")}
          </DialogTitle>
          <DialogDescription>
            {t("detail.qcModal.subtitle", { id: String(lotId) })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0 space-y-1">
              <Label className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
                {t("detail.qcModal.kind")}
              </Label>
              <Select value={kind} onValueChange={(v) => setKind(v as QCKind)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LAB">
                    {t("detail.qcModal.kindLab")}
                  </SelectItem>
                  <SelectItem value="CLIENT">
                    {t("detail.qcModal.kindClient")}
                  </SelectItem>
                  <SelectItem value="OTHER">
                    {t("detail.qcModal.kindOther")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-0 space-y-1">
              <Label className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
                <span>{t("detail.qcModal.contract")}</span>
                <span className="text-muted-foreground/70 ml-1.5 text-[10px] font-normal lowercase tracking-normal">
                  {t("detail.qcModal.contractOptional")}
                </span>
              </Label>
              <Select value={contractId} onValueChange={setContractId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("detail.qcModal.pickContract")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t("detail.qcModal.pickContract")}
                  </SelectItem>
                  {(contracts ?? []).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.clientName ?? "—"} — {c.contractNumber ?? `#${c.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
              {t("detail.qcModal.decision")}
            </Label>
            <Select
              value={decision}
              onValueChange={(v) => setDecision(v as QCDecision)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACCEPTED">
                  {t("detail.decision.ACCEPTED")}
                </SelectItem>
                <SelectItem value="REJECTED">
                  {t("detail.decision.REJECTED")}
                </SelectItem>
                <SelectItem value="PENDING">
                  {t("detail.decision.PENDING")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {decision === "REJECTED" ? (
            <div className="space-y-1">
              <Label className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
                {t("detail.qcModal.reason")}
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("detail.qcModal.reasonPlaceholder")}
                rows={2}
              />
            </div>
          ) : null}

          <div className="space-y-1">
            <Label className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
              <span>{t("detail.qcModal.note")}</span>
              <span className="text-muted-foreground/70 ml-1.5 text-[10px] font-normal lowercase tracking-normal">
                {t("detail.qcModal.noteOptional")}
              </span>
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("detail.qcModal.notePlaceholder")}
              rows={3}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
              {t("detail.qcModal.attachments")}
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={onPickFiles}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground inline-flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs transition-colors"
            >
              <Upload className="size-3.5" />
              {files.length === 0
                ? t("detail.qcModal.attach")
                : t("detail.qcModal.addMore")}
            </button>
            {files.length > 0 ? (
              <ul className="overflow-hidden rounded-md border">
                {files.map((pf, i) => (
                  <li
                    key={`${pf.file.name}-${i}`}
                    className={`flex items-center gap-2 px-3 py-2 text-xs ${
                      i === files.length - 1 ? "" : "border-b"
                    }`}
                  >
                    <FileText className="text-muted-foreground size-3.5 flex-shrink-0" />
                    <span className="flex-1 truncate">{pf.file.name}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {formatFileSize(pf.file.size)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-muted-foreground hover:text-destructive rounded p-0.5"
                      aria-label={t("detail.qcModal.remove")}
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {uploading ? (
            <Input
              readOnly
              value={t("detail.qcModal.uploading", { n: files.length })}
              className="text-muted-foreground"
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            {t("detail.qcModal.cancel")}
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {t("detail.qcModal.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function formatFileSize(n: number): string {
  if (!n || n <= 0) return "—"
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
